import type { DocumentSegment } from "../../../../api/content/sourcesApi";
import type { InstanceStatement } from "../../../../api/knowledgeData/knowledgeDataDecoders";
import type { AcceptedResult } from "../../../../api/knowledgeData/lensDecoders";
import type { ShownStatement } from "../../../knowledgeData/abox/aboxViewModel";

/**
 * A document's regest as Daniel's viewer lays it out (F94; HisQu/Ontology-Development
 * `src/viewer/regest_viewer.py` at `4afd98e0`, carried with his permission, RP-1): each part of
 * the text (a head, a regest) with the statements on it, in reading order; only parts that have
 * some (F64). What a statement says is the instance-data view's (`aboxViewModel`).
 */

/** Statements the regest draws: accepted ones, or a result not accepted, such as a proposal under review (F154). */
export interface RegestResult extends AcceptedResult {
  readonly accepted: boolean;
}

export interface PlacedStatement {
  /** `<result>#<number>`: unique on the page. */
  readonly key: string;
  /** The result it belongs to, which opens its page. */
  readonly source: string;
  /** The commit of the model version it was written against, where its terms are named and open. */
  readonly modelCommit: string;
  /** Whether its result is accepted; a proposal's statements are told apart (F154). */
  readonly accepted: boolean;
  readonly statement: InstanceStatement;
}

export interface RegestPlacement {
  /** By segment path (`""` for a document without segments), in reading order within the part. */
  readonly parts: ReadonlyMap<string, readonly PlacedStatement[]>;
  /** Those with no words in this text: a review still shows them (W-15 anchors evidence in other documents too). */
  readonly unplaced: readonly PlacedStatement[];
}

/**
 * Each statement in the part that holds most of its words in `document` (`<document>@<version>`;
 * offsets are document-wide).
 */
export function regestParts(document: string, segments: readonly DocumentSegment[], results: readonly RegestResult[]): RegestPlacement {
  const parts = segments.length === 0
    ? [{ segmentPath: "", start: 0, end: Number.MAX_SAFE_INTEGER }]
    : segments;
  const placed = new Map<string, { readonly offset: number; readonly statement: PlacedStatement }[]>();
  const unplaced: PlacedStatement[] = [];
  for (const result of results) {
    for (const statement of result.statements) {
      const shown: PlacedStatement = {
        key: `${result.source}#${String(statement.number)}`,
        source: result.source,
        modelCommit: result.model.slice(result.model.indexOf("@") + 1),
        accepted: result.accepted,
        statement,
      };
      const spans = statement.evidence
        .filter((span) => span.segment.split("#")[0] === document)
        .map((span) => ({ span, part: parts.findIndex((part) => part.start <= span.start && span.start < part.end) }));
      const part = parts[mostCommon(spans.map((span) => span.part).filter((index) => index >= 0)) ?? -1];
      if (part === undefined) {
        unplaced.push(shown);
        continue;
      }
      const here = spans.filter((span) => parts[span.part] === part).map((span) => span.span.start);
      placed.set(part.segmentPath, [...(placed.get(part.segmentPath) ?? []), { offset: Math.min(...here), statement: shown }]);
    }
  }
  return {
    parts: new Map(parts.flatMap(({ segmentPath }) => {
      const statements = placed.get(segmentPath);
      return statements === undefined ? [] : [[segmentPath, [...statements].sort((a, b) => a.offset - b.offset).map((item) => item.statement)] as const];
    })),
    unplaced,
  };
}

/** The keys a shown line answers for: its own statement and those it shows by a value. */
export function itemKeys(item: ShownStatement<PlacedStatement>): string[] {
  return [item.shown.key, ...item.absorbed.map((absorbed) => absorbed.key)];
}

function mostCommon(values: readonly number[]): number | null {
  const counts = new Map<number, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  let best: number | null = null;
  for (const [value, count] of counts) if (best === null || count > (counts.get(best) ?? 0)) best = value;
  return best;
}
